import React, { useState, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Paper, 
  Divider, 
  //Chip,
  Grid,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { 
  Mic as MicIcon, 
  MicOff as MicOffIcon, 
  Send as SendIcon, 
  Upload as UploadIcon, 
  Link as LinkIcon,
  Save as SaveIcon,
  Help as HelpIcon,
  AttachFile as AttachFileIcon,
  TextFields as TextFieldsIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import GradientText from '../common/GradientText.tsx';
import VoiceWaveform from '../VoiceWaveform.tsx';

interface ProblemSourcingDrawerProps {
  onSubmit: (problem: string) => void;
  onClose?: () => void;
}

const DrawerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.7)',
  backdropFilter: 'blur(1px)',
  borderBottom: '1px solid rgba(138, 43, 226, 0.3)',
  position: 'relative',
  maxHeight: '85vh',
  overflowY: 'auto',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(to bottom, #00CC99, #8A2BE2)',
  },
}));

const InputMethodsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 20px rgba(138, 43, 226, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #8A2BE2 90%)',
    boxShadow: '0 6px 25px rgba(138, 43, 226, 0.5)',
    transform: 'translateY(-2px)',
  },
  '&.Mui-disabled': {
    background: 'rgba(138, 43, 226, 0.3)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(26, 26, 46, 0.4)',
    borderRadius: theme.spacing(1),
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8A2BE2',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
  '& .MuiOutlinedInput-input': {
    color: '#fff',
  },
}));

const VoiceRecordingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 204, 153, 0.2))',
    transform: 'rotate(-45deg)',
    transition: 'all 0.5s ease',
    opacity: 0,
  },
  '&:hover': {
    boxShadow: '0 0 15px rgba(0, 204, 153, 0.2)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
}));

const RecordButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  color: '#FF6F00',
  padding: theme.spacing(1.5),
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  '&.recording': {
    backgroundColor: 'rgba(255, 111, 0, 0.2)',
    color: '#FF6F00',
    animation: '$pulse 1.5s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(255, 111, 0, 0.4)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(255, 111, 0, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(255, 111, 0, 0)',
    },
  },
}));

const AIPromptContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'rgba(138, 43, 226, 0.1)',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(138, 43, 226, 0.2)',
  },
}));

const AIPromptText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.9)',
  fontStyle: 'italic',
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: 'rgba(0, 51, 102, 0.3)',
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 5px 15px rgba(0, 204, 153, 0.2)',
  },
}));

const FileUploadInput = styled('input')({
  display: 'none',
});

const ProblemSourcingDrawer: React.FC<ProblemSourcingDrawerProps> = ({ 
  onSubmit, 
  onClose 
}) => {
  const [inputMethod, setInputMethod] = useState<'voice' | 'text' | 'upload' | 'link'>('voice');
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [problemText, setProblemText] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string>('');
  
  // AI prompts
  const aiPrompts = [
    'Consider including specific metrics or KPIs that would define success',
    'What constraints or limitations should be considered?',
    'Are there any existing solutions that partially address this problem?'
  ];
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleInputMethodChange = (method: 'voice' | 'text' | 'upload' | 'link') => {
    setInputMethod(method);
  };
  
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    
    if (!isRecording) {
      // Start recording logic would go here
      // For demo, we'll just simulate a recording after 3 seconds
      setTimeout(() => {
        setProblemText('Our customer service response times have increased by 45% over the last quarter, leading to decreased satisfaction scores. We need an AI-powered solution that can handle routine inquiries while maintaining a personal touch.');
        setIsRecording(false);
      }, 3000);
    } else {
      // Stop recording logic would go here
    }
  };
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      setUploadedFile(files[0]);
      // In a real app, we would parse the file content
      setProblemText(`Uploaded file: ${files[0].name} - Content would be extracted and analyzed here.`);
    }
  };
  
  const handleLinkSubmit = () => {
    if (sourceUrl) {
      // In a real app, we would fetch and parse the content from the URL
      setProblemText(`Content from link: ${sourceUrl} - Content would be extracted and analyzed here.`);
    }
  };
  
  const handleSubmit = () => {
    if (problemText.trim()) {
      onSubmit(problemText);
    }
  };
  
  const handleSaveDraft = () => {
    // Logic to save draft would go here
    alert('Problem draft saved!');
  };
  
  return (
    <DrawerContainer>
      <Box>
        <GradientText gradient="primary" variant="h6">
          Describe Your Problem
        </GradientText>
        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 1 }}>
          Be specific and include relevant details to get the best solutions
        </Typography>
      </Box>

      <Divider sx={{ mb: 3, backgroundColor: 'rgba(138, 43, 226, 0.3)' }} />

      <InputMethodsContainer>
        <GradientButton
          variant={inputMethod === 'voice' ? 'contained' : 'outlined'}
          startIcon={<MicIcon />}
          onClick={() => handleInputMethodChange('voice')}
          sx={{ 
            borderColor: 'rgba(138, 43, 226, 0.5)',
            color: inputMethod === 'voice' ? '#fff' : 'rgba(255, 255, 255, 0.8)',
            background: inputMethod === 'voice' ? 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' : 'transparent',
          }}
        >
          Voice
        </GradientButton>
        
        <GradientButton
          variant={inputMethod === 'text' ? 'contained' : 'outlined'}
          startIcon={<TextFieldsIcon />}
          onClick={() => handleInputMethodChange('text')}
          sx={{ 
            borderColor: 'rgba(138, 43, 226, 0.5)',
            color: inputMethod === 'text' ? '#fff' : 'rgba(255, 255, 255, 0.8)',
            background: inputMethod === 'text' ? 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' : 'transparent',
          }}
        >
          Text
        </GradientButton>
        
        <GradientButton
          variant={inputMethod === 'upload' ? 'contained' : 'outlined'}
          startIcon={<UploadIcon />}
          onClick={() => handleInputMethodChange('upload')}
          sx={{ 
            borderColor: 'rgba(138, 43, 226, 0.5)',
            color: inputMethod === 'upload' ? '#fff' : 'rgba(255, 255, 255, 0.8)',
            background: inputMethod === 'upload' ? 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' : 'transparent',
          }}
        >
          Upload
        </GradientButton>
        
        <GradientButton
          variant={inputMethod === 'link' ? 'contained' : 'outlined'}
          startIcon={<LinkIcon />}
          onClick={() => handleInputMethodChange('link')}
          sx={{ 
            borderColor: 'rgba(138, 43, 226, 0.5)',
            color: inputMethod === 'link' ? '#fff' : 'rgba(255, 255, 255, 0.8)',
            background: inputMethod === 'link' ? 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)' : 'transparent',
          }}
        >
          Link
        </GradientButton>
        
        <Box sx={{ flexGrow: 1 }} />
        
        <Tooltip title="Get help with problem formulation">
          <IconButton size="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </InputMethodsContainer>
      
      <Divider sx={{ mb: 3, backgroundColor: 'rgba(138, 43, 226, 0.3)' }} />

      {inputMethod === 'text' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          
          <StyledTextField
            fullWidth
            multiline
            rows={4}
            label="Describe your problem in detail"
            variant="outlined"
            value={problemText}
            onChange={(e) => setProblemText(e.target.value)}
            placeholder="What challenge are you trying to solve? Be as specific as possible..."
          />
        </motion.div>
      )}
      
      {inputMethod === 'voice' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <VoiceRecordingContainer>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isRecording && <VoiceWaveform isActive={isRecording} />}
              
              <RecordButton 
                className={isRecording ? 'recording' : ''}
                onClick={toggleRecording}
              >
                {isRecording ? <MicOffIcon /> : <MicIcon />}
              </RecordButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ 
                color: isRecording ? '#FF6F00' : 'rgba(255, 255, 255, 0.9)',
                fontWeight: isRecording ? 600 : 400,
                display: 'relative',
                alignItems: 'center'
              }}>
                {isRecording ? (
                  <>
                    <Box 
                      component="span" 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: '#FF6F00', 
                        mr: 1,
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { opacity: 1 },
                          '50%': { opacity: 0.4 },
                          '100%': { opacity: 1 },
                        }
                      }} 
                    />
                    Recording... Speak clearly
                  </>
                ) : 'Click the microphone to start recording'}
              </Typography>
              
              {isRecording && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Speak your problem statement clearly. Click the microphone again to stop recording.
                </Typography>
              )}
            </Box>
          </VoiceRecordingContainer>
          
          {isRecording && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(255, 111, 0, 0.1)', 
              borderRadius: 1, 
              mb: 2,
              border: '1px dashed rgba(255, 111, 0, 0.3)',
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                <strong>Tips for better voice recognition:</strong>
              </Typography>
              <ul style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: 8, paddingLeft: 20 }}>
                <li>Speak clearly and at a moderate pace</li>
                <li>Minimize background noise</li>
                <li>Use specific terms and avoid ambiguity</li>
              </ul>
            </Box>
          )}
        </motion.div>
      )}
      
      {inputMethod === 'upload' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <FileUploadInput
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ 
                  borderColor: 'rgba(138, 43, 226, 0.5)',
                  color: '#fff',
                  '&:hover': {
                    borderColor: '#8A2BE2',
                    background: 'rgba(138, 43, 226, 0.1)',
                  },
                  px: 3,
                  py: 1.5,
                  borderRadius: '20px',
                }}
              >
                Upload Document
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ 
                  borderColor: 'rgba(0, 204, 153, 0.5)',
                  color: '#fff',
                  '&:hover': {
                    borderColor: '#00CC99',
                    background: 'rgba(0, 204, 153, 0.1)',
                  },
                  px: 3,
                  py: 1.5,
                  borderRadius: '20px',
                }}
              >
                Browse Files
              </Button>
            </Box>
            
            {uploadedFile && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(138, 43, 226, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" color="#fff">
                  File: {uploadedFile.name} ({Math.round(uploadedFile.size / 1024)} KB)
                </Typography>
              </Box>
            )}
            
            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" sx={{ mt: 2 }}>
              Supported formats: PDF, DOC, DOCX, TXT
            </Typography>
          </Box>
        </motion.div>
      )}
      
      {inputMethod === 'link' && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <StyledTextField
                fullWidth
                label="Paste URL"
                variant="outlined"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://..."
              />
            </Grid>
            <Grid item xs={3}>
              <GradientButton
                fullWidth
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={handleLinkSubmit}
                sx={{ 
                  height: '56px',
                }}
              >
                Extract
              </GradientButton>
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* AI Prompts */}
      {(inputMethod === 'text' || inputMethod === 'voice') && (
        <Box sx={{ mt: 3 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#00CCFF',
              textShadow: '0 0 10px rgba(0, 204, 255, 0.5)',
              mb: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            AI Suggestions to Improve Your Problem Statement:
          </Typography>
          
          {aiPrompts.map((prompt, index) => (
            <AIPromptContainer key={index}>
              <AIPromptText variant="body2">
                {prompt}
              </AIPromptText>
            </AIPromptContainer>
          ))}
        </Box>
      )}
      
      {/* Preview if there's text */}
      {problemText && (
        <PreviewContainer>
          <Typography variant="subtitle2" sx={{ color: 'rgba(0, 204, 153, 0.9)', mb: 1 }}>
            Problem Summary:
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            {problemText}
          </Typography>
        </PreviewContainer>
      )}
      
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveDraft}
          sx={{ 
            borderColor: 'rgba(0, 204, 153, 0.5)',
            color: '#00CC99',
            '&:hover': {
              borderColor: '#00CC99',
              backgroundColor: 'rgba(0, 204, 153, 0.1)',
            }
          }}
        >
          Save Draft
        </Button>
        
        <GradientButton
          variant="contained"
          endIcon={<SendIcon />}
          onClick={handleSubmit}
          disabled={!problemText.trim()}
        >
          Submit Problem
        </GradientButton>
      </Box>

      <IconButton 
        onClick={onClose} 
        aria-label="close drawer"
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        <CloseIcon />
      </IconButton>
    </DrawerContainer>
  );
};

export default ProblemSourcingDrawer; 